import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard/ServiceCard';
import SEO from '../components/SEO/SEO';
import StructuredData from '../components/StructuredData/StructuredData';
import { servicesData } from '../data/servicesData';
import styles from './ServicesPage.module.css';

const ServicesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [resultsCount, setResultsCount] = useState(servicesData.length);
  const [scrollProgress, setScrollProgress] = useState(0);
  const searchInputRef = useRef(null);

  // Get unique categories from data, including 'All'
  const categories = useMemo(() => 
    ['All', ...new Set(servicesData.map(s => s.category))], 
    []
  );

  // Filter services based on selected category and search term
  const filteredServices = useMemo(() => {
    return servicesData.filter(service => {
      const matchesCategory = selectedCategory === 'All' || service.category === selectedCategory;
      const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          service.tagline.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    setResultsCount(filteredServices.length);
  }, [filteredServices]);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={styles.servicesPage}>
      <SEO 
        title="Software Development Services | SolveDot Labs - AI, IoT, Robotics & Web Development"
        description="SolveDot Labs offers comprehensive software development services: AI Solutions, website development, IoT systems, robotics solutions, mobile app development, data analytics, manufacturing software (MES, Industry 4.0), CRM development, and custom software development. 99% uptime guarantee."
        keywords="solve, solvedot, solvedotlabs, solvedot labs, solve dot labs, solvedot labs pvt ltd, SolveDot Labs, AI, Software, Software Development, website development, web development, iot, robotics, software company, AI Solutions, Software Development Services, IoT Solutions, Robotics Solutions, Web Development Services, Custom Software Development"
      />
      <StructuredData type="services" />
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      {/* Enhanced Hero Header */}
      <section className={styles.heroHeader}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOrb1}></div>
          <div className={styles.heroOrb2}></div>
        </div>
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>Our Services</span>
          <h1 className={styles.heroTitle}>A Universe of Solutions, Tailored for Your Success</h1>
          <p className={styles.heroDescription}>
            You have the vision. We have the technology to make it a reality. Explore our services to find the perfect solution to your biggest challenges.
          </p>
        </div>
      </section>

      {/* Enhanced Filter and Search UI */}
      <section className={styles.filtersSection}>
        <div className={styles.filtersContainer}>
          <div className={styles.searchWrapper}>
            <div className={styles.searchIcon}>🔍</div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search services..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className={styles.clearSearch}
                onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          
          <div className={styles.categoryFilters}>
            <span className={styles.filterLabel}>Filter by Category:</span>
            {categories.map(category => (
              <button
                key={category}
                className={`${styles.filterButton} ${selectedCategory === category ? styles.activeFilter : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        <div className={styles.resultsInfo}>
          <span className={styles.resultsCount}>
            {resultsCount} {resultsCount === 1 ? 'Service' : 'Services'} Found
          </span>
          {searchTerm && (
            <span className={styles.searchTerm}>
              for "<strong>{searchTerm}</strong>"
            </span>
          )}
        </div>
      </section>

      {/* Services Grid */}
      <section className={styles.servicesSection}>
        {filteredServices.length > 0 ? (
          <div className={styles.servicesGrid}>
            {filteredServices.map((service, index) => (
              <Link 
                key={service.id} 
                to={`/services/${service.id}`} 
                className={styles.cardLink}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <ServiceCard 
                  icon={service.icon}
                  title={service.title}
                  description={service.tagline}
                />
              </Link>
            ))}
          </div>
        ) : (
          <div className={styles.noResults}>
            <div className={styles.noResultsIcon}>🔍</div>
            <h3>No services found</h3>
            <p>Try adjusting your search or filter criteria</p>
            <button 
              className={styles.clearFilters}
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All');
              }}
            >
              Clear All Filters
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ServicesPage;
